/**
 * QUÉ VISITA FALTA — etiquetas y colores, en un solo lugar.
 *
 * El backend (`lib/tipoDeEspera.ts`) deriva el tipo de la evidencia y lo manda como `wait_kind`;
 * acá solo se traduce y se pinta. Lo comparten el board de Waiting to Schedule y el panel
 * "esperando que se agende" de los dashboards de Compañía y Scheduling: tres pantallas que
 * responden la misma pregunta y no pueden contestarla distinto.
 *
 * El color agrupa por trabajo: gris = hay que hacer la primera visita, rojo = el equipo pide otra
 * mirada después de una inspección hecha, morado = es una llamada, accent = ATR.
 */
export const ESPERA_ETIQUETAS: Record<string, { clave: string; claveEtapa?: string; color: string }> = {
  adjuster_meeting: { clave: 'cdash.tagAdjuster', claveEtapa: 'cdash.tagClaimIn', color: 'var(--text-2)' },
  reinspection: { clave: 'cdash.tagReinspection', color: 'var(--red)' },
  welcome_call: { clave: 'cdash.tagWelcome', color: 'var(--purple)' },
  atr: { clave: 'cdash.tagAtr', color: 'var(--accent)' },
};

/**
 * DOS PANTALLAS, DOS PREGUNTAS — mismo tipo, distinta palabra.
 *
 * En el BOARD de Waiting to Schedule la tarjeta responde "¿qué visita agendo?" → Adjuster meeting.
 * En el DASHBOARD el panel responde "¿en qué etapa está esperando?" → Claim in, que es como el
 * dueño lee su pipeline. Es el mismo `wait_kind` del backend: solo cambia la etiqueta, así que las
 * dos pantallas no pueden clasificar distinto (que era el problema original).
 */
const etiquetaCon = (campo: 'clave' | 'claveEtapa') => (t: (k: string) => string) => (extra: string) => {
  const e = ESPERA_ETIQUETAS[extra];
  if (!e) return { texto: extra, color: 'var(--text-2)' };
  return { texto: t((campo === 'claveEtapa' ? e.claveEtapa : undefined) ?? e.clave), color: e.color };
};

/** Panel de los dashboards: la ETAPA en la que espera. */
export const etiquetaDeEspera = etiquetaCon('claveEtapa');
/** Board de agendado: QUÉ VISITA hay que hacer. */
export const etiquetaDeVisita = etiquetaCon('clave');

/**
 * LAS TRES PESTAÑAS de "esperando que se agende" (pedido del dueño 2026-09-17, textual: "quites de las
 * gráficas en waiting to schedule los reinspection request, ponlos en la misma gráfica pero con una pestaña
 * que diga reinspection; adjuster meeting muestra los otros, por defecto; también la pestaña de ATR").
 *
 * Re-inspección y ATR tienen pestaña propia; TODO lo demás (Claim in, Insurance inspection, Needs Reschedule,
 * Welcome Call…) cae en Adjuster meeting, que es la primera y la que se abre. La misma partición vive en el
 * backend del televisor (modules/tv/scheduling): dos pantallas, una sola regla, los mismos números.
 */
export type EsperaPestana = 'adjuster_meeting' | 'reinspection' | 'atr';
export const ESPERA_PESTANAS: ReadonlyArray<{ clave: EsperaPestana; i18n: string }> = [
  { clave: 'adjuster_meeting', i18n: 'espera.tabAdjuster' },
  { clave: 'reinspection', i18n: 'espera.tabReinspection' },
  { clave: 'atr', i18n: 'espera.tabAtr' },
];
export const pestanaDeEspera = (tipo: string | null | undefined): EsperaPestana => (tipo === 'reinspection' || tipo === 'atr' ? tipo : 'adjuster_meeting');
